import type { ComponentType } from "react";
import React from "react";
import type { LicenseModule } from "../utils/license";

// Lazy load all pages for better code splitting
const DemandsPage = React.lazy(() => import("../pages/admin/DemandsPage"));

const ProductionSchedulesPage = React.lazy(() => import("../pages/scheduling/ProductionSchedulesPage"));

const ResourcesPage = React.lazy(() => import("../pages/admin/ResourcesPage"));
const ResourceTypesPage = React.lazy(() => import("../pages/admin/ResourceTypesPage"));

const OvenSetupTimesPage = React.lazy(() => import("../pages/admin/OvenSetupTimesPage"));
const ProcessingLineSetupTimesPage = React.lazy(() => import("../pages/admin/ProcessingLineSetupTimesPage"));
const ProductRoutingsPage = React.lazy(() => import("../pages/admin/ProductRoutingsPage"));
const IntermediateRoutingsPage = React.lazy(() => import("../pages/admin/IntermediateRoutingsPage"));

const HolidaysPage = React.lazy(() => import("../pages/admin/HolidaysPage"));
const SystemSettingsPage = React.lazy(() => import("../pages/admin/SystemSettingsPage"));
const CsvImportConfigPage = React.lazy(() => import("../pages/admin/CsvImportConfigPage"));
const ExcelExportConfigPage = React.lazy(() => import("../pages/admin/ExcelExportConfigPage"));
const UserManagementPage = React.lazy(() => import("../pages/admin/UserManagementPage"));
const ProductionOrdersPage = React.lazy(() => import("../pages/admin/ProductionOrdersPage"));

const ProductsPage = React.lazy(() => import("../pages/admin/ProductsPage"));
const IntermediateProductsPage = React.lazy(() => import("../pages/admin/IntermediateProductsPage"));
const RawMaterialsPage = React.lazy(() => import("../pages/admin/RawMaterialsPage"));
const DeliveriesPage = React.lazy(() => import("../pages/admin/DeliveriesPage"));

const MaintenanceWindowsPage = React.lazy(() => import("../pages/admin/MaintenanceWindowsPage"));

const ShiftModelTemplatesPage = React.lazy(() => import("../pages/admin/ShiftModelTemplatesPage"));

const UserRolesPage = React.lazy(() => import("../pages/admin/UserRolesPage"));

const DebugTestFunctionsPage = React.lazy(() => import("../pages/admin/DebugTestFunctionsPage"));
const GanttVisualizationProfilesPage = React.lazy(() => import("../pages/admin/GanttVisualizationProfilesPage"));
const HomePage = React.lazy(() => import("../pages/HomePage"));
const PermissionsPage = React.lazy(() => import("../pages/admin/PermissionsPage"));
const ShiftPlanPage = React.lazy(() => import("../pages/scheduling/ShiftPlanPage"));
const CurrentPlanPage = React.lazy(() => import("../pages/scheduling/CurrentPlanPage"));
const CampaignSequencesPage = React.lazy(() => import("../pages/admin/CampaignSequencesPage"));
const ClientErrorsPage = React.lazy(() => import("../pages/admin/ClientErrorsPage"));
const LicensePage = React.lazy(() => import("../pages/admin/LicensePage"));
const DemoTenantsPage = React.lazy(() => import("../pages/admin/DemoTenantsPage"));
const StocksPage = React.lazy(() => import("../pages/materials/StocksPage"));
const IntermediateStocksPage = React.lazy(() => import("../pages/materials/IntermediateStocksPage"));

export type PerspectiveAppRoutes = {
  perspective : 'administration' | 'scheduling' | 'planning';
  labelKey : string;
  appRoutes : AppRoute[];
}

export type AppRoute = {
  nodeId: string;
  labelKey: string;
  path: string; // optional, folders won't have this
  component: ComponentType<any>; // only for leaf routes
  icon?: string;
  roles: string[];
  children?: AppRoute[];
  /** Licensed module the page belongs to. Without a licence it is hidden and shows NotLicensedPage. */
  module?: LicenseModule;
};

export const perspectiveAppRoutes: PerspectiveAppRoutes[] = [
  {
    perspective: 'administration',
    labelKey: 'routes.administration',
    appRoutes: [
      {
        nodeId: "2",
        labelKey: "routes.orderData",
        path: "order-data",
        icon: "e-icons e-folder",
        component: HomePage, // Placeholder for folder
        roles: ["admin"],
        children: [
          {
            nodeId: "2.1",
            labelKey: "routes.productionOrders",
            path: "production-orders",
            component: ProductionOrdersPage,
            icon: "e-icons e-print-layout",
            roles: ["admin"],
            module: "SCHEDULING",
          },
          {
            nodeId: "2.2",
            labelKey: "routes.demand",
            path: "demand",
            component: DemandsPage,
            icon: "e-icons e-chart",
            roles: ["admin"],
          },
          // Production order optimization page removed
        ],
      },
      {
        nodeId: "3",
        labelKey: "routes.materialData",
        path: "material-data",
        icon: "e-icons e-folder",
        component: HomePage, // Placeholder for folder
        roles: ["admin"],
        children: [
          {
            nodeId: "3.1",
            labelKey: "routes.products",
            path: "products",
            component: ProductsPage,
            icon: "e-icons e-notes",
            roles: ["admin"],
          },
          {
            nodeId: "3.1.5",
            labelKey: "routes.intermediateProducts",
            path: "intermediate-products",
            component: IntermediateProductsPage,
            icon: "e-icons e-notes",
            roles: ["admin"],
          },
          {
            nodeId: "3.2",
            labelKey: "routes.rawMaterials",
            path: "raw-materials",
            component: RawMaterialsPage,
            icon: "e-icons e-notes",
            roles: ["admin"],
          },
          {
            nodeId: "3.3",
            labelKey: "routes.stocks",
            path: "stocks",
            component: StocksPage,
            icon: "e-icons e-user-defined",
            roles: ["admin"],
          },
          {
            nodeId: "3.3.1",
            labelKey: "routes.intermediateStocks",
            path: "intermediate-stocks",
            component: IntermediateStocksPage,
            icon: "e-icons e-user-defined",
            roles: ["admin"],
          },
          {
            nodeId: "3.4",
            labelKey: "routes.deliveries",
            path: "deliveries",
            component: DeliveriesPage,
            icon: "e-icons e-travel-and-places",
            roles: ["admin"],
          },
          // {
          //   nodeId: "3.5",
          //   labelKey: "routes.maxStock",
          //   path: "max-stock",
          //   component: MaxStockPage,
          //   icon: "e-icons e-arrow-up",
          //   roles: ["admin"],
          // },
          // {
          //   nodeId: "3.6",
          //   labelKey: "routes.minStock",
          //   path: "min-stock",
          //   component: MinStockPage,
          //   icon: "e-icons e-arrow-down",
          //   roles: ["admin"],
          // },
        ],
      },
      {
        nodeId: "4",
        labelKey: "routes.resourceData",
        path: "resource-data",
        icon: "e-icons e-folder",
        component: HomePage, // Placeholder for folder
        roles: ["admin"],
        children: [
          {
            nodeId: "4.1",
            labelKey: "routes.resourceTypes",
            path: "resource-types",
            component: ResourceTypesPage,
            icon: "e-icons e-shapes",
            roles: ["admin"],
          },
          {
            nodeId: "4.2",
            labelKey: "routes.resources",
            path: "resources",
            component: ResourcesPage,
            icon: "e-icons e-transform",
            roles: ["admin"],
          },
          {
            nodeId: "4.3",
            labelKey: "routes.maintenanceWindows",
            path: "maintenance-windows",
            component: MaintenanceWindowsPage,
            icon: "e-icons e-flags",
            roles: ["admin"],
          },
          {
            nodeId: "4.4",
            labelKey: "routes.shiftModelTemplates",
            path: "shift-model-templates",
            component: ShiftModelTemplatesPage,
            icon: "e-icons e-table-2",
            roles: ["admin"],
          },
        ],
      },
      {
        nodeId: "5",
        labelKey: "routes.routingData",
        path: "routing-data",
        icon: "e-icons e-folder",
        component: HomePage, // Placeholder for folder
        roles: ["admin"],
        children: [
          {
            nodeId: "5.1",
            labelKey: "routes.productRoutings",
            path: "product-routings",
            component: ProductRoutingsPage,
            icon: "e-icons e-xml-mapping",
            roles: ["admin"],
          },
          {
            nodeId: "5.1.5",
            labelKey: "routes.intermediateRoutings",
            path: "intermediate-routings",
            component: IntermediateRoutingsPage,
            icon: "e-icons e-xml-mapping",
            roles: ["admin"],
          },
          // {
          //   nodeId: "5.2",
          //   labelKey: "routes.setupTimes",
          //   path: "setup-times",
          //   component: SetupTimesPage,
          //   icon: "e-icons e-clock",
          //   roles: ["admin"],
          // },
          {
            nodeId: "5.3",
            labelKey: "routes.ovenSetupTimes",
            path: "oven-setup-times",
            component: OvenSetupTimesPage,
            icon: "e-icons e-clock",
            roles: ["admin"],
          },
          {
            nodeId: "5.4",
            labelKey: "routes.processingLineSetupTimes",
            path: "processing-line-setup-times",
            component: ProcessingLineSetupTimesPage,
            icon: "e-icons e-clock",
            roles: ["admin"],
          },
        ],
      },
      {
        nodeId: "6",
        labelKey: "routes.settings",
        path: "settings",
        icon: "e-icons e-folder",
        component: HomePage, // Placeholder for folder
        roles: ["admin"],
        children: [
          {
            nodeId: "6.1",
            labelKey: "routes.general",
            path: "general",
            icon: "e-icons e-folder",
            component: HomePage, // Placeholder for folder
            roles: ["admin"],
            children: [
              {
                nodeId: "6.1.1",
                labelKey: "routes.systemSettings",
                path: "system-settings",
                component: SystemSettingsPage,
                icon: "e-icons e-settings",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.2",
                labelKey: "routes.userManagement",
                path: "user-management",
                component: UserManagementPage,
                icon: "e-icons e-user",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.3",
                labelKey: "routes.userRoles",
                path: "user-roles",
                component: UserRolesPage,
                icon: "e-icons e-people",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.4",
                labelKey: "routes.holidays",
                path: "holidays",
                component: HolidaysPage,
                icon: "e-icons e-month",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.6",
                labelKey: "routes.ganttVisualization",
                path: "gantt-visualization-profiles",
                component: GanttVisualizationProfilesPage,
                icon: "e-icons e-settings",
                roles: ["admin"],
                module: "SCHEDULING",
              },
              {
                nodeId: "6.1.7",
                labelKey: "routes.csvImportConfig",
                path: "csv-import-configs",
                component: CsvImportConfigPage,
                icon: "e-icons e-settings",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.8",
                labelKey: "routes.excelExportConfig",
                path: "excel-export-configs",
                component: ExcelExportConfigPage,
                icon: "e-icons e-settings",
                roles: ["admin"],
              },
              {
                nodeId: "6.1.9",
                labelKey: "routes.license",
                path: "license",
                component: LicensePage,
                icon: "e-icons e-lock",
                roles: ["admin"],
              },
              {
                // Only on the Prod demo server (profile flag demoServer), see treeBuilder.
                nodeId: "6.1.10",
                labelKey: "routes.demoTenants",
                path: "demo-tenants",
                component: DemoTenantsPage,
                icon: "e-icons e-refresh",
                roles: ["admin"],
              },
            ],
          },
          {
            nodeId: "6.3",
            labelKey: "routes.supportSection",
            path: "support",
            icon: "e-icons e-folder",
            component: HomePage, // Placeholder for folder
            roles: ["admin"],
            children: [
              {
                nodeId: "6.3.1",
                labelKey: "routes.debugTestFunctions",
                path: "debug-test-functions",
                component: DebugTestFunctionsPage,
                icon: "e-icons e-search",
                roles: ["admin"],
              },
              {
                nodeId: "6.3.4",
                labelKey: "routes.clientErrors",
                path: "client-errors",
                component: ClientErrorsPage,
                icon: "e-icons e-warning",
                roles: ["admin"],
              },
            ],
          },
        ],
      },
      {
        nodeId: "7",
        labelKey: "routes.permissions",
        path: "permissions",
        component: PermissionsPage,
        icon: "e-icons e-lock",
        roles: ["admin"],
      },
    ]
  },
  {
    perspective: 'scheduling',
    labelKey: 'routes.scheduling',
    appRoutes: [
      {
        nodeId: "8",
        labelKey: "routes.shiftPlan",
        path: "shift-plan",
        component: ShiftPlanPage,
        icon: "e-icons e-timeline-work-week",
        roles: ["admin"],
      },
      // {
      //   nodeId: "9",
      //   labelKey: "routes.demandView",
      //   path: "demand-view",
      //   component: DemandViewPage,
      //   icon: "e-icons e-chart",
      //   roles: ["admin"],
      // },
      {
        nodeId: "10",
        labelKey: "routes.productionSchedules",
        path: "production-schedules",
        component: ProductionSchedulesPage,
        icon: "e-icons e-xml-mapping",
        roles: ["admin"],
        module: "SCHEDULING",
      },
      {
        nodeId: "11",
        labelKey: "routes.currentPlan",
        path: "current-plan",
        component: CurrentPlanPage,
        icon: "e-icons e-table",
        roles: ["admin"],
        module: "SCHEDULING",
      },
      {
        nodeId: "12",
        labelKey: "routes.campaignSequences",
        path: "campaign-sequences",
        component: CampaignSequencesPage,
        icon: "e-icons e-sort-ascending",
        roles: ["admin"],
        module: "SCHEDULING",
      }
    ]
  },
  // Planning perspective removed (production plan visualization deleted)
];

export const flattenRoutes = (perspectiveRoutes: PerspectiveAppRoutes[]): AppRoute[] => {
  const flattenPerspectiveRoutes = (perspectiveRoute: PerspectiveAppRoutes): AppRoute[] => {
    const flatRoutes: AppRoute[] = [];

    const walk = (routes: AppRoute[], parentPath: string) => {
      for (const route of routes) {
        
        // Build current path by combining parent path and current route path
        const currentPath = `${parentPath}/${route.path}`;
        
        const newRoute: AppRoute = {
          nodeId: route.nodeId,
          labelKey: route.labelKey,
          path: currentPath,
          component: route.component,
          icon: route.icon || "",
          roles: route.roles,
          children: route.children,
          module: route.module,
        };

        if (!route.children) {
          flatRoutes.push(newRoute);
        }

        if (route.children) {
          walk(route.children, currentPath);
        }
      }
    };

    walk(perspectiveRoute.appRoutes, '/' + perspectiveRoute.perspective);
    return flatRoutes;
  };

  return perspectiveRoutes.flatMap(pr => flattenPerspectiveRoutes(pr));
};

